import { useEffect, useRef, useState } from "react";
import { socket } from "../../../sockets";
import { getQuotes } from "../actions/getQuotes";

export const useQuotes = () => {
  const localToken = sessionStorage.getItem("token");
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const quoteIdRef = useRef(null);
  // Last fetch args, used to honor active filter/pagination when refreshing on external events.
  const lastFetchArgsRef = useRef({ status: null, page: undefined, pageSize: undefined, includeArchived: false, archivedOnly: false });

  const getAllQuotes = async (status, { page, pageSize, includeArchived, archivedOnly } = {}) => {
    lastFetchArgsRef.current = { status, page, pageSize, includeArchived, archivedOnly };
    setLoading(true);
    setError(null);
    try {
      const data = await getQuotes({ authToken: localToken, status, page, pageSize, includeArchived, archivedOnly });
      setQuotes(data);
    } catch (err) {
      console.error("❌ Error fetching quotes:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // External refresh hook: anything that mutates a Quote (e.g. invoice totalCost edit) can
  // dispatch `flownect:quotes-updated` on window and the table re-fetches with the latest
  // filter/pagination without forcing a full page reload.
  useEffect(() => {
    const handler = () => {
      if (quotes && Array.isArray(quotes?.items)) {
        const { status, page, pageSize, includeArchived, archivedOnly } = lastFetchArgsRef.current;
        getAllQuotes(status, { page, pageSize, includeArchived, archivedOnly });
      }
    };
    window.addEventListener("flownect:quotes-updated", handler);
    return () => window.removeEventListener("flownect:quotes-updated", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes]);

  const getQuoteById = async (quoteId, customToken, genericToken = false) => {
    if (!quoteId) return;
    setLoading(true);
    setError(null);

    const authToken = customToken ?? localToken;
    quoteIdRef.current = quoteId;

    try {
      const data = await getQuotes({ authToken, quoteId, genericToken });
      setSelectedQuote(data);
    } catch (err) {
      console.error("❌ Error fetching quote:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!quoteIdRef.current) return;

    const quoteId = quoteIdRef.current;
    const roomName = `room_quote_${quoteId}`;

    socket.emit("joinQuoteRoom", quoteId);
    console.log(`Joined ${roomName}`);

    const handleQuoteUpdate = (updatedQuote) => {
      if (!updatedQuote) return;
      const updateId = String(updatedQuote?.id ?? updatedQuote?.quoteId ?? "");
      if (updateId !== String(quoteId)) return;

      console.log(`📡 Quote ${updateId} updated via socket`);
      setSelectedQuote((prev) => ({
        ...prev,
        ...updatedQuote,
      }));
    };

    socket.on("updateQuoteStatus", handleQuoteUpdate);

    return () => {
      socket.emit("leaveQuoteRoom", quoteId);
      socket.off("updateQuoteStatus", handleQuoteUpdate);
      console.log(`Left ${roomName}`);
    };
  }, [selectedQuote?.id]);

  return {
    // Actions
    getAllQuotes,
    getQuoteById,

    // States
    quotes,
    selectedQuote,
    loading,
    error,
  };
};
